import { CheckCircle2, CircleAlert } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { ToastContext } from './toast-context'

interface ToastEntry {
  id: number
  message: string
  kind: 'success' | 'error'
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const nextId = useRef(0)

  const showToast = useCallback((message: string, kind: 'success' | 'error' = 'success') => {
    const id = nextId.current++
    setToasts((current) => [...current, { id, message, kind }])
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, 2_500)
  }, [])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 &&
        createPortal(
          <div className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-6 sm:bottom-8">
            {toasts.map((toast) => (
              <div
                key={toast.id}
                role="status"
                className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-white shadow-lg ${
                  toast.kind === 'success'
                    ? 'from-primary to-secondary bg-gradient-to-r'
                    : 'bg-danger-solid'
                }`}
              >
                {toast.kind === 'success' ? (
                  <CheckCircle2 className="size-4 shrink-0" />
                ) : (
                  <CircleAlert className="size-4 shrink-0" />
                )}
                {toast.message}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  )
}
